import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Zap, Layers, Activity } from 'lucide-react';
import { GridEditorState, GridEditorActions } from '../GridEditorPresenter';
import { ToolsPanel } from './sidebar/ToolsPanel';
import { GeoJSONPanel } from './sidebar/GeoJSONPanel';
import { ErrorPanel } from './sidebar/ErrorPanel';

interface SidebarProps {
  state: GridEditorState;
  actions: GridEditorActions;
}

const LayerToggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between py-1.5 cursor-pointer group">
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span className="text-sm text-slate-700 group-hover:text-slate-900">{label}</span>
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
        checked ? 'bg-blue-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? 'translate-x-[18px]' : 'translate-x-[3px]'
        }`}
      />
    </button>
  </label>
);

export const Sidebar: React.FC<SidebarProps> = ({
  state,
  actions
}) => {
  const [isMapControlsOpen, setIsMapControlsOpen] = useState(true);

  const {
    elements,
    selectedTool,
    connectionStart,
    mapBackground,
    mapCenter,
    geoJSONLayers,
    saveErrors,
    lastSaveResult,
    isEditMode,
    layerVisibility,
    metricsVisibility
  } = state;

  return (
    <div className="w-64 h-full bg-white border-r border-slate-200 flex flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-50 rounded-lg">
            <Zap className="h-5 w-5 text-blue-600" />
          </div>
          <h1 className="text-base font-semibold text-slate-800">Plan BT</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Network Layers */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-800">Calques du réseau</h2>
          </div>
          <div className="space-y-0.5">
            <LayerToggle
              label="Poteaux"
              checked={layerVisibility.poles}
              onChange={(v) => actions.setLayerVisibility('poles', v)}
            />
            <LayerToggle
              label="Compteurs"
              checked={layerVisibility.meters}
              onChange={(v) => actions.setLayerVisibility('meters', v)}
            />
            <LayerToggle
              label="Transformateurs"
              checked={layerVisibility.transformers}
              onChange={(v) => actions.setLayerVisibility('transformers', v)}
            />
            <LayerToggle
              label="Sous-stations"
              checked={layerVisibility.substations}
              onChange={(v) => actions.setLayerVisibility('substations', v)}
            />
          </div>
        </div>

        {/* Metrics Bubble Visibility */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-800">Affichage des métriques</h2>
          </div>
          <div className="space-y-0.5">
            <LayerToggle
              label="Tension / Puissance"
              checked={metricsVisibility.voltagePower}
              onChange={(v) => actions.setMetricsVisibility('voltagePower', v)}
            />
            <LayerToggle
              label="RSSI"
              checked={metricsVisibility.rssi}
              onChange={(v) => actions.setMetricsVisibility('rssi', v)}
            />
            <LayerToggle
              label="Anneau en ligne / hors-ligne"
              checked={metricsVisibility.statusRing}
              onChange={(v) => actions.setMetricsVisibility('statusRing', v)}
            />
          </div>
        </div>

        {/* Map Controls */}
        <div className="px-4 py-3 border-b border-slate-100">
          <button
            onClick={() => setIsMapControlsOpen(!isMapControlsOpen)}
            className="flex items-center justify-between w-full mb-2"
          >
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 3v18" />
              </svg>
              <h2 className="text-sm font-semibold text-slate-800">Contrôles de la carte</h2>
            </div>
            {isMapControlsOpen ? (
              <ChevronUp className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </button>

          {isMapControlsOpen && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Type de carte
                </label>
                <select
                  value={mapBackground}
                  onChange={(e) => actions.setMapBackground(e.target.value as any)}
                  className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                >
                  <option value="osm">OpenStreetMap</option>
                  <option value="satellite">Satellite (ESRI)</option>
                  <option value="terrain">Terrain (OpenTopo)</option>
                  <option value="street">Rue (CartoDB)</option>
                  <option value="none">Aucune carte</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Latitude centre
                  </label>
                  <input
                    type="number"
                    value={mapCenter.lat}
                    onChange={(e) => actions.setMapCenter({ ...mapCenter, lat: parseFloat(e.target.value) })}
                    step="0.0001"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Longitude centre
                  </label>
                  <input
                    type="number"
                    value={mapCenter.lng}
                    onChange={(e) => actions.setMapCenter({ ...mapCenter, lng: parseFloat(e.target.value) })}
                    step="0.0001"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tools - only in edit mode */}
        {isEditMode && (
          <ToolsPanel
            selectedTool={selectedTool}
            connectionStart={connectionStart}
            actions={actions}
          />
        )}

        {/* GeoJSON Layers - only in edit mode */}
        {isEditMode && (
          <GeoJSONPanel
            geoJSONLayers={geoJSONLayers}
            mapCenter={mapCenter}
            elements={elements}
            actions={actions}
          />
        )}

        {/* Error Panel */}
        <ErrorPanel
          saveErrors={saveErrors}
          lastSaveResult={lastSaveResult}
          actions={actions}
        />
      </div>
    </div>
  );
};
